<template>
    <div class="questionnaire-step-final">
        <div
            :class="classContentWrapper"
            class="content-wrapper"
        >
            <Transition
                appear
                mode="out-in"
                name="form"
            >
                <div
                    v-if="submitOnLoad"
                    class="loader form"
                >
                    <h1 class="title">
                        <!-- TODO translate -->
                        Loading... please wait
                    </h1>
                </div>

                <QuestionnaireAuth
                    v-else
                    :errors
                    :sign-up-step
                    :fetching-new-customer-submit="fetching"
                    @login-success="handleLoginSuccess"
                    @submit-new-customer="submitNewCustomer"
                />
            </Transition>

            <div class="image-container">
                <Transition
                    appear
                    name="pill"
                >
                    <div class="image-wrapper">
                        <div class="rounded-image">
                            <CmsImage
                                :data="signUpStep.image"
                                fit="cover"
                                is-eager
                            />
                        </div>
                    </div>
                </Transition>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, toRef } from 'vue'
import { storeToRefs } from 'pinia'

import type {
    QuestionnaireFragment,
    QuestionnaireSignUpStepFragment,
} from '@/graphql/api'

import { useQuestionnaireSubmit } from './useQuestionnaireSubmit'
import { useCustomerRegistrationInputStore } from '@/composables/useCustomerRegistrationInput'
import { useLastResponseErrorAtPath } from '@/helpers/errors'
import { cmsColorToButtonColor, useCmsLinkItemAction } from '../..'
import { getTimeout } from '@/helpers/globals'
import { useQuestionnaireStore } from './useQuestionnaireStore'
import { useIsAdmin } from '@/components/app/isAdmin'
import { useAuth } from '@/plugins/auth.ts'
import { useQuestionnaireCompleteNewCustomerMutation } from '@/graphql/api/mutations/questionnaireCompleteNewCustomer.mutation.generated.ts'
import { useQuestionnaireCompleteExistingCustomerMutation } from '@/graphql/api/mutations/questionnaireCompleteExistingCustomer.mutation.generated.ts'

import CmsImage from '@/components/cms/CmsImage.vue'
import QuestionnaireAuth from './QuestionnaireAuth.vue'

const props = defineProps<{
    signUpStep: QuestionnaireSignUpStepFragment
    questionnaire: QuestionnaireFragment
}>()

const emit = defineEmits<{
    submit: []
}>()

const newCustomerMutation = useQuestionnaireCompleteNewCustomerMutation()
const { executeMutation: executeMutationNewCustomer, fetching } =
    newCustomerMutation

const existingCustomerMutation =
    useQuestionnaireCompleteExistingCustomerMutation()
const { executeMutation: executeMutationExistingCustomer } =
    existingCustomerMutation

const errors = useLastResponseErrorAtPath([
    {
        response: newCustomerMutation,
        path: 'questionnaireCompleteNewCustomer',
    },
    {
        response: existingCustomerMutation,
        path: 'questionnaireCompleteExistingCustomer',
    },
])

const { customerRegistrationInput } = storeToRefs(
    useCustomerRegistrationInputStore()
)

const questionnaireCompleteInput = useQuestionnaireSubmit(
    toRef(() => props.questionnaire)
)

const { clearQuestionnaireAnswers } = useQuestionnaireStore()

const cmsLinkItemAction = useCmsLinkItemAction()
const onSubmit = async () => {
    // Let the parent know first so that
    // currentStepIndex reactivity is disabled and no intermediate
    // blinking of step 0 occurs
    emit('submit')

    await cmsLinkItemAction(props.questionnaire.redirectLink)

    clearQuestionnaireAnswers(props.questionnaire.id)
}

// Make sure the loader is visible for a bit even if the mutation has resolved
const LOADER_ANIMATION_DURATION = 3000
let animationStart: null | number = null

onMounted(() => {
    animationStart = Date.now()
})

const { setTimeout, clearTimeout } = getTimeout() ?? {
    setTimeout: undefined,
    clearTimeout: undefined,
}

let redirectTimeoutId: null | number = null

onBeforeUnmount(() => {
    if (redirectTimeoutId) {
        clearTimeout?.(redirectTimeoutId)
    }
})

const { isLogged, setAuth } = useAuth()

const input = questionnaireCompleteInput.value

const submitOnLoad = Boolean(input) && isLogged.value

const submitExistingCustomer = () => executeMutationExistingCustomer({ input })

if (submitOnLoad) {
    submitExistingCustomer().then(() => {
        if (animationStart) {
            const elapsed = Date.now() - animationStart

            if (elapsed > LOADER_ANIMATION_DURATION) {
                onSubmit()
            } else if (setTimeout) {
                redirectTimeoutId = setTimeout(
                    onSubmit,
                    LOADER_ANIMATION_DURATION - elapsed
                )
            }
        } else {
            onSubmit()
        }
    })
}

const handleLoginSuccess = () => {
    submitExistingCustomer().then(onSubmit)
}

const isAdmin = useIsAdmin()

const submitNewCustomer = async () => {
    const input = questionnaireCompleteInput.value
    const customer = customerRegistrationInput.value

    if (!input || !customer) {
        return
    }

    executeMutationNewCustomer({
        input,
        customer,
        isConsultant: isAdmin.value,
    }).then((response) => {
        const customer =
            response.data?.questionnaireCompleteNewCustomer.customer

        if (!customer) {
            return
        }

        setAuth(customer.login)

        onSubmit()
    })
}

const imageBackgroundColor = computed(() =>
    cmsColorToButtonColor(props.signUpStep.backgroundColor)
)

const classContentWrapper = computed(() => ({
    'submit-on-load': submitOnLoad,
}))
</script>

<style lang="scss" scoped>
@property --s {
    syntax: '<percentage>';
    inherits: false;
    initial-value: 20%;
}

@property --x {
    syntax: '<percentage>';
    inherits: false;
    initial-value: 20%;
}

@property --y {
    syntax: '<percentage>';
    inherits: false;
    initial-value: 20%;
}

.questionnaire-step-final {
    width: 100%;
    position: absolute;
    inset: 0;
    height: 100%;
    overflow: auto;

    .content-wrapper {
        width: 100%;
        min-height: 100%;
        position: relative;
        display: flex;
        overflow: hidden;

        &.submit-on-load {
            --x: 20%;
            --y: 20%;
            --s: 20%;

            background:
                radial-gradient(
                    ellipse at var(--x) var(--y),
                    transparent,
                    v-bind(imageBackgroundColor)
                ),
                radial-gradient(
                    ellipse at calc(100% - var(--x)) calc(100% - var(--y)),
                    transparent,
                    var(--slate)
                );
            // url('https://grainy-gradients.vercel.app/noise.svg')
            animation-duration: 6.5s, 1.5s;
            animation-name: gr, appear;
            animation-iteration-count: infinite, 1;
            animation-direction: normal;
            animation-timing-function: linear, ease-out;
        }

        @keyframes appear {
            0% {
                opacity: 0;
            }

            100% {
                opacity: 1;
            }
        }

        @keyframes gr {
            0% {
                --x: 0%;
                --y: 50%;
                --s: 20%;
            }

            25% {
                --x: 50%;
                --y: 0%;
                --s: 22%;
            }

            50% {
                --x: 100%;
                --y: 50%;
                --s: 25%;
            }

            75% {
                --x: 50%;
                --y: 100%;
                --s: 22%;
            }

            100% {
                --x: 0%;
                --y: 50%;
                --s: 20%;
            }
        }

        .loader {
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            color: black;
            font-family: $effra-font-stack;
            text-transform: uppercase;
            letter-spacing: 0.01em;
            font-weight: 400;
            font-size: 1rem;

            .title {
                // padding: 8px 12px 12px 8px;
                padding: 12px 16px;
                background-color: rgb(255 255 255 / 20%);
            }
        }

        .image-container {
            width: 100%;
            display: none;

            @include min-width(1024) {
                display: block;
            }

            .image-wrapper {
                width: 100%;
                height: 100%;
                background-image: linear-gradient(
                    90deg,
                    v-bind(imageBackgroundColor),
                    v-bind(imageBackgroundColor)
                );
                background-position: left 168px top 0;
                background-repeat: no-repeat;
                display: flex;
                align-items: center;
            }

            .rounded-image {
                width: 336px;
                max-width: 100%;
                height: 500px;
                border-radius: 200px;
                overflow: hidden;
                background-repeat: no-repeat;
                background-size: cover;
                background-position: center center;
                position: relative;
                z-index: 2;
            }
        }
    }
}

// This step has custom transitions
.pill-enter-active,
.pill-leave-active {
    transition: all 0.85s var(--timing-function-easing-decelerate);
}

.pill-enter-from,
.pill-leave-to {
    opacity: 0;
    transform: translateX(40px);
}

.form-enter-active,
.form-leave-active {
    transition: all 0.95s var(--timing-function-easing-decelerate) 0.35s;
}

.form-enter-from,
.form-leave-to {
    opacity: 0;
    transform: translateY(40px);
}

// Alternative choreography
// (comment out the above transitions to try it out)

// .pill-enter-active,
// .pill-leave-active {
//     transition: all .45s var(--timing-function-easing-emphasized);
// }

// .pill-enter-from,
// .pill-leave-to {
//     opacity: 0;
//     transform: translateX(180px);
// }

// .form-enter-active,
// .form-leave-active {
//     transition: all .85s var(--timing-function-easing-decelerate) .45s;
// }

// .form-enter-from,
// .form-leave-to {
//     opacity: 0;
//     transform: translateY(40px);
// }
</style>
